import React, { forwardRef, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { MegaMenuCSSCustomProperties, fadeTransitions, linkHoverTransition, primaryNavigationId, } from './MegaMenu.config';
import Logo from '@polestar/component-warehouse-react/components/logo/Logo';
import { useMegaMenu } from './MegaMenuContext';
import { disappearingHeaderTranslateStyle, dividerLineStyle, mobileInlinePadding, tabletInlinePadding, } from './MegaMenu.styles';
import MenuButton from '@polestar/component-warehouse-react/components/menu-button/MenuButton';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { createCSSPropertyTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { css } from '@emotion/react';
// todo: set the height of the header as prop for teams and css
// this header will contain the "visible height" of the menu. This will probably be used by teams to determine certain offsets
// We probably want to dynamically make this value available and listen with the resize observer
// I will set the value hardcoded for now on the MegaMenuNavigation component
/**
 * Header contains the logo and menu button
 */
export const MegaMenuHeaderBar = forwardRef(({ logoProps, menuButtonProps, megaMenuId, ariaLabels, ...props }, ref) => {
    const { setMenuActive, menuActive, setFlyoutActive } = useMegaMenu();
    const [menuHasBeenOpen, setMenuHasBeenOpen] = useState(false);
    const menuButtonRef = useRef(null);
    const timeoutRef = useRef(null);
    const { queryMatches } = useMediaQuery();
    const handleClick = () => {
        setMenuHasBeenOpen(true);
        setMenuActive(true);
        setFlyoutActive(false);
    };
    useEffect(() => {
        if (!queryMatches.desktop && !menuActive && menuButtonRef.current && menuHasBeenOpen) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = window.setTimeout(() => {
                if (menuButtonRef.current) {
                    menuButtonRef.current.focus();
                }
            }, 0);
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [queryMatches.desktop, menuActive, menuHasBeenOpen]);
    return (React.createElement(Header, { ref: ref, ...props },
        React.createElement(StyledLogo, { ...logoProps, as: 'a', ...aria({ label: ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.backToHomepage }) }),
        React.createElement(ButtonWrapper, null,
            React.createElement(MenuButton, { onClick: handleClick, "data-type": "menu-button", ref: menuButtonRef, ...menuButtonProps, ...aria({
                    label: ariaLabels.openMenu,
                    expanded: menuActive,
                    controls: `${megaMenuId}-${primaryNavigationId}`,
                }), controlsNavigationDrawer: false }))));
});
/**
 * This style block is used for creating the fullwidth background style for mobile and tablet
 * It uses a before and absolute positioning to be able to bleed outside its container
 */
const fullWidthBackgroundStyles = css `
  &::before {
    content: '';
    inset: 0 -${mobileInlinePadding};
    position: absolute;
    background: var(${MegaMenuCSSCustomProperties.background});
    z-index: -1;

    ${MediaQueries.tablet} {
      inset: 0 -${tabletInlinePadding};
    }
  }
`;
const Header = styled.header `
  position: relative;
  inline-size: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  align-self: start;
  margin: auto;
  pointer-events: auto;

  ${MediaQueries.untilDesktop} {
    ${fullWidthBackgroundStyles};
    ${dividerLineStyle};
    ${disappearingHeaderTranslateStyle};
    transition: ${createCSSPropertyTransition('background-color', fadeTransitions)};
    padding-block: ${Spacings.medium};

    ${MediaQueries.tablet} {
      padding-block: ${createCustomSpacing(20 / 16)};
    }
  }
`;
const StyledLogo = styled(Logo) `
  margin: -${Spacings.xxSmall} 0;
  cursor: pointer;
  transition: ${createCSSPropertyTransition('color', fadeTransitions)}, ${linkHoverTransition};
`;
const ButtonWrapper = styled.div `
  ${MediaQueries.desktop} {
    display: none;
  }
`;
